import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users,
    UserPlus,
    UserCheck,
    UserX,
    Edit2,
    Clock,
    Calendar,
    Search,
    RefreshCw,
    X,
    Check,
    Loader2,
    MoreVertical,
    Mail,
    AlertTriangle
} from 'lucide-react';
import { getMembers, getAvailableUsers, addMember, updateMember, removeMember } from '../../services/fieldTeamApi';
import './TeamManagement.css';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.05 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
    }
};

const tableRowVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: { delay: i * 0.03, duration: 0.2 }
    }),
    exit: { opacity: 0, x: 10, transition: { duration: 0.2 } }
};

function TeamManagement() {
    // Data state
    const [members, setMembers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [saving, setSaving] = useState(false);

    // Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [includeInactive, setIncludeInactive] = useState(false);

    // Dropdown menu
    const [activeMenu, setActiveMenu] = useState(null);

    // Form state for add/edit
    const [formData, setFormData] = useState({
        user_id: '',
        working_hours_start: '09:00',
        working_hours_end: '18:00',
        working_days: ['mon', 'tue', 'wed', 'thu', 'fri']
    });

    // Load data on mount and when includeInactive changes
    useEffect(() => {
        fetchMembers();
    }, [includeInactive]);

    useEffect(() => {
        fetchAvailableUsers();
    }, []);

    const fetchMembers = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const response = await getMembers({ include_inactive: includeInactive });
            setMembers(response.data.data || []);
        } catch (err) {
            console.error('Failed to fetch members:', err);
            setMembers([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchAvailableUsers = async () => {
        try {
            const response = await getAvailableUsers();
            setAvailableUsers(response.data.users || []);
        } catch (err) {
            console.error('Failed to fetch available users:', err);
            setAvailableUsers([]);
        }
    };

    // Form handlers
    const resetFormData = () => {
        setFormData({
            user_id: '',
            working_hours_start: '09:00',
            working_hours_end: '18:00',
            working_days: ['mon', 'tue', 'wed', 'thu', 'fri']
        });
    };

    const toggleDay = (day) => {
        setFormData(prev => ({
            ...prev,
            working_days: prev.working_days.includes(day)
                ? prev.working_days.filter(d => d !== day)
                : [...prev.working_days, day]
        }));
    };

    // Modal handlers
    const openAddModal = () => {
        resetFormData();
        fetchAvailableUsers(); // Refresh available users
        setShowAddModal(true);
        setActiveMenu(null);
    };

    const openEditModal = (member) => {
        setSelectedMember(member);
        setFormData({
            user_id: member.user_id,
            working_hours_start: member.working_hours_start || '09:00',
            working_hours_end: member.working_hours_end || '18:00',
            working_days: parseWorkingDays(member.working_days)
        });
        setShowEditModal(true);
        setActiveMenu(null);
    };

    const openDeleteModal = (member) => {
        setSelectedMember(member);
        setShowDeleteModal(true);
        setActiveMenu(null);
    };

    const closeModals = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        setSelectedMember(null);
        resetFormData();
    };

    // CRUD handlers
    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!formData.user_id) return;

        setSaving(true);
        try {
            await addMember({
                user_id: parseInt(formData.user_id),
                working_hours_start: formData.working_hours_start,
                working_hours_end: formData.working_hours_end,
                working_days: formData.working_days
            });
            closeModals();
            fetchMembers();
            fetchAvailableUsers(); // Refresh available users list
        } catch (err) {
            console.error('Failed to add member:', err);
            alert('Error adding member: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleEditMember = async (e) => {
        e.preventDefault();
        if (!selectedMember) return;

        setSaving(true);
        try {
            await updateMember(selectedMember.id, {
                working_hours_start: formData.working_hours_start,
                working_hours_end: formData.working_hours_end,
                working_days: formData.working_days
            });
            closeModals();
            fetchMembers();
        } catch (err) {
            console.error('Failed to update member:', err);
            alert('Error updating member: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (member) => {
        try {
            await updateMember(member.id, {
                is_active: !member.is_active
            });
            fetchMembers();
        } catch (err) {
            console.error('Failed to toggle member status:', err);
            alert('Error updating member status');
        }
        setActiveMenu(null);
    };

    const handleDeleteMember = async () => {
        if (!selectedMember) return;

        setSaving(true);
        try {
            await removeMember(selectedMember.id);
            closeModals();
            fetchMembers();
            fetchAvailableUsers(); // Refresh available users list
        } catch (err) {
            console.error('Failed to remove member:', err);
            alert('Error removing member: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    // Stats
    const stats = useMemo(() => {
        const total = members.length;
        const active = members.filter(m => m.is_active).length;
        const inactive = total - active;
        return { total, active, inactive };
    }, [members]);

    // Filtered members
    const filteredMembers = useMemo(() => {
        return members.filter(m => {
            const search = searchTerm.toLowerCase();
            return (
                (m.name?.toLowerCase().includes(search) ||
                    m.username?.toLowerCase().includes(search) ||
                    m.email?.toLowerCase().includes(search))
            );
        });
    }, [members, searchTerm]);

    // Helper functions
    const parseWorkingDays = (days) => {
        if (!days) return ['mon', 'tue', 'wed', 'thu', 'fri'];
        if (typeof days === 'string') {
            try {
                return JSON.parse(days);
            } catch {
                return ['mon', 'tue', 'wed', 'thu', 'fri'];
            }
        }
        return days;
    };

    const formatWorkingDays = (days) => {
        const parsed = parseWorkingDays(days);
        if (!parsed || parsed.length === 0) return '-';
        return parsed.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
    };

    const formatWorkingHours = (start, end) => {
        if (!start && !end) return '-';
        return `${start || '09:00'} - ${end || '18:00'}`;
    };

    const dayLabels = [
        { key: 'mon', label: 'Mon' },
        { key: 'tue', label: 'Tue' },
        { key: 'wed', label: 'Wed' },
        { key: 'thu', label: 'Thu' },
        { key: 'fri', label: 'Fri' },
        { key: 'sat', label: 'Sat' },
        { key: 'sun', label: 'Sun' }
    ];

    return (
        <motion.div
            className="team-management-page"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Page Header */}
            <motion.div className="field-team-page-header" variants={itemVariants}>
                <div>
                    <h1>Team Management</h1>
                    <p>Manage your field team members and their settings</p>
                </div>
                <div className="header-actions">
                    <motion.button
                        className="btn btn-ghost btn-icon"
                        onClick={() => fetchMembers(true)}
                        disabled={refreshing}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Refresh"
                    >
                        <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
                    </motion.button>
                    <motion.button
                        className="btn btn-primary"
                        onClick={openAddModal}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <UserPlus size={16} /> Add Member
                    </motion.button>
                </div>
            </motion.div>

            {/* Stat Blocks */}
            <motion.div className="stat-blocks" variants={itemVariants}>
                <div className="stat-block">
                    <div className="stat-icon"><Users size={20} /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total Members</span>
                    </div>
                </div>
                <div className="stat-block">
                    <div className="stat-icon"><UserCheck size={20} /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats.active}</span>
                        <span className="stat-label">Active</span>
                    </div>
                </div>
                <div className="stat-block">
                    <div className="stat-icon"><UserX size={20} /></div>
                    <div className="stat-content">
                        <span className="stat-value">{stats.inactive}</span>
                        <span className="stat-label">Inactive</span>
                    </div>
                </div>
            </motion.div>

            {/* Toolbar */}
            <motion.div className="team-toolbar" variants={itemVariants}>
                <div className="toolbar-left">
                    <div className="input-with-icon">
                        <Search size={16} className="icon" />
                        <input
                            type="text"
                            placeholder="Search members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input"
                        />
                    </div>
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)}
                        />
                        <span>Show inactive</span>
                    </label>
                </div>
                <div className="toolbar-right">
                    <span className="results-count">{filteredMembers.length} members</span>
                </div>
            </motion.div>

            {/* Members Table */}
            <motion.div className="table-container" variants={itemVariants}>
                {loading ? (
                    <div className="loading-state">
                        <Loader2 size={32} className="spin" />
                        <p>Loading team members...</p>
                    </div>
                ) : filteredMembers.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Users size={32} />
                        </div>
                        <h3>No members found</h3>
                        <p>{searchTerm ? 'Try adjusting your search' : 'Add your first team member to get started'}</p>
                        {!searchTerm && (
                            <button className="btn btn-primary" onClick={openAddModal}>
                                <UserPlus size={16} /> Add Member
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th><Clock size={14} /> Working Hours</th>
                                    <th><Calendar size={14} /> Working Days</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence mode="popLayout">
                                    {filteredMembers.map((member, index) => (
                                        <motion.tr
                                            key={member.id}
                                            variants={tableRowVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            custom={index}
                                            layout
                                            className={!member.is_active ? 'inactive-row' : ''}
                                        >
                                            <td>
                                                <div className="member-name-cell">
                                                    <div className="member-avatar">
                                                        {(member.name || member.username)?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                    <div className="member-info">
                                                        <span className="name">{member.name || member.username}</span>
                                                        <span className="username">@{member.username}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="email-cell">
                                                    {member.email ? (
                                                        <>
                                                            <Mail size={14} />
                                                            <span>{member.email}</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                                                    {member.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="text-muted">
                                                {formatWorkingHours(member.working_hours_start, member.working_hours_end)}
                                            </td>
                                            <td className="text-muted">
                                                {formatWorkingDays(member.working_days)}
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="actions-cell">
                                                    <button
                                                        className="btn btn-icon btn-ghost"
                                                        onClick={(e) => {
                                                            if (activeMenu?.id === member.id) {
                                                                setActiveMenu(null);
                                                                return;
                                                            }
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            const spaceBelow = window.innerHeight - rect.bottom;
                                                            const shouldOpenUp = spaceBelow < 200;
                                                            setActiveMenu({
                                                                id: member.id,
                                                                member: member,
                                                                top: shouldOpenUp ? rect.top - 160 : rect.bottom + 4,
                                                                left: rect.left - 160
                                                            });
                                                        }}
                                                    >
                                                        <MoreVertical size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>

                        {/* Fixed position dropdown menu */}
                        <AnimatePresence>
                            {activeMenu && (
                                <motion.div
                                    className="dropdown-menu-fixed"
                                    style={{
                                        position: 'fixed',
                                        top: activeMenu.top,
                                        left: activeMenu.left,
                                        zIndex: 9999
                                    }}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    <button onClick={() => openEditModal(activeMenu.member)}>
                                        <Edit2 size={14} /> Edit Member
                                    </button>
                                    <button onClick={() => handleToggleActive(activeMenu.member)}>
                                        {activeMenu.member.is_active ? (
                                            <><UserX size={14} /> Deactivate</>
                                        ) : (
                                            <><UserCheck size={14} /> Activate</>
                                        )}
                                    </button>
                                    <div className="dropdown-divider" />
                                    <button
                                        className="danger"
                                        onClick={() => openDeleteModal(activeMenu.member)}
                                    >
                                        <X size={14} /> Remove Member
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </motion.div>

            {/* Menu backdrop */}
            {activeMenu && <div className="menu-backdrop" onClick={() => setActiveMenu(null)} />}

            {/* Add Member Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModals}
                    >
                        <motion.div
                            className="modal"
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <div className="modal-icon-wrapper">
                                    <UserPlus size={24} />
                                </div>
                                <h2 className="modal-title">Add Team Member</h2>
                                <button className="modal-close" onClick={closeModals}>
                                    <X size={20} />
                                </button>
                            </div>
                            <form onSubmit={handleAddMember}>
                                <div className="modal-body">
                                    <div className="form-group">
                                        <label>Select User</label>
                                        <select
                                            value={formData.user_id}
                                            onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                                            required
                                            className="input select"
                                        >
                                            <option value="">Choose a user...</option>
                                            {availableUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name || user.username} ({user.email || user.username})
                                                </option>
                                            ))}
                                        </select>
                                        {availableUsers.length === 0 && (
                                            <p className="form-hint">No available users. All users are already team members.</p>
                                        )}
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Working Hours Start</label>
                                            <input
                                                type="time"
                                                value={formData.working_hours_start}
                                                onChange={(e) => setFormData({ ...formData, working_hours_start: e.target.value })}
                                                className="input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Working Hours End</label>
                                            <input
                                                type="time"
                                                value={formData.working_hours_end}
                                                onChange={(e) => setFormData({ ...formData, working_hours_end: e.target.value })}
                                                className="input"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Working Days</label>
                                        <div className="days-checkboxes">
                                            {dayLabels.map(({ key, label }) => (
                                                <label
                                                    key={key}
                                                    className={`day-checkbox ${formData.working_days.includes(key) ? 'active' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.working_days.includes(key)}
                                                        onChange={() => toggleDay(key)}
                                                    />
                                                    <span>{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                        Cancel
                                    </button>
                                    <motion.button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || !formData.user_id}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                                        Add Member
                                    </motion.button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Edit Member Modal */}
            <AnimatePresence>
                {showEditModal && selectedMember && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModals}
                    >
                        <motion.div
                            className="modal"
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <div className="modal-icon-wrapper">
                                    <Edit2 size={24} />
                                </div>
                                <h2 className="modal-title">Edit Team Member</h2>
                                <button className="modal-close" onClick={closeModals}>
                                    <X size={20} />
                                </button>
                            </div>
                            <form onSubmit={handleEditMember}>
                                <div className="modal-body">
                                    <div className="member-edit-info">
                                        <div className="member-avatar large">
                                            {(selectedMember.name || selectedMember.username)?.charAt(0).toUpperCase() || 'U'}
                                        </div>
                                        <div className="member-details">
                                            <h3>{selectedMember.name || selectedMember.username}</h3>
                                            <p>@{selectedMember.username}</p>
                                            {selectedMember.email && <p className="email">{selectedMember.email}</p>}
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Working Hours Start</label>
                                            <input
                                                type="time"
                                                value={formData.working_hours_start}
                                                onChange={(e) => setFormData({ ...formData, working_hours_start: e.target.value })}
                                                className="input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Working Hours End</label>
                                            <input
                                                type="time"
                                                value={formData.working_hours_end}
                                                onChange={(e) => setFormData({ ...formData, working_hours_end: e.target.value })}
                                                className="input"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Working Days</label>
                                        <div className="days-checkboxes">
                                            {dayLabels.map(({ key, label }) => (
                                                <label
                                                    key={key}
                                                    className={`day-checkbox ${formData.working_days.includes(key) ? 'active' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.working_days.includes(key)}
                                                        onChange={() => toggleDay(key)}
                                                    />
                                                    <span>{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                        Cancel
                                    </button>
                                    <motion.button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                                        Save Changes
                                    </motion.button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && selectedMember && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModals}
                    >
                        <motion.div
                            className="modal"
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header danger">
                                <div className="modal-icon-wrapper danger">
                                    <AlertTriangle size={24} />
                                </div>
                                <h2 className="modal-title">Remove Team Member</h2>
                                <button className="modal-close" onClick={closeModals}>
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="delete-warning">
                                    <p>Are you sure you want to remove <strong>{selectedMember.name || selectedMember.username}</strong> from the team?</p>
                                    <p className="text-muted">This will remove all their team settings. Their user account will remain intact.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={closeModals}>
                                    Cancel
                                </button>
                                <motion.button
                                    className="btn btn-danger"
                                    onClick={handleDeleteMember}
                                    disabled={saving}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {saving ? <Loader2 size={16} className="spin" /> : <X size={16} />}
                                    Remove Member
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default TeamManagement;
